import React, { useEffect } from 'react';
import { useIsDarkMode } from '../../hooks/useIsDarkMode';
import { generatePublicImagePath } from './generatePublicImagePath';
import { generatePublicImagePathFallback } from './generatePublicImagePathFallback';
export type ImageProps = {
  /**
   * a node to be rendered in the special component.
   */
  type?: 'plerionLogo' | 'provider' | 'resourceType' | 'framework' | 'others';
  name?: string;
  fileExtension?: string;
  src?: string;
  baseUrl?: string;
  fallback?: Omit<ImageProps, 'fallback'> & {
    className?: string;
    alt?: string;
  };
};

// Cache images paths that failed to resolve
const IMAGE_ERROR_MAP: {
  [key: string]: true;
} = {};

export const Image = React.forwardRef<
  HTMLImageElement,
  React.PropsWithChildren<
    ImageProps & React.ImgHTMLAttributes<HTMLImageElement>
  >
>(function InputWithRef(
  {
    type,
    name,
    src,
    fileExtension,
    fallback,
    alt,
    title,
    className,
    baseUrl,
    ...otherProps
  },
  ref,
) {
  const dark = useIsDarkMode();
  const publicImagePath = generatePublicImagePath({
    type,
    name,
    baseUrl,
    src,
    fileExtension,
    dark,
  });
  const [hasError, setHasError] = React.useState(false);

  useEffect(() => {
    setHasError(false);
  }, [publicImagePath]);

  const shouldShowFallback = hasError || IMAGE_ERROR_MAP[publicImagePath];

  const typeFallbackImage = type
    ? generatePublicImagePathFallback({ type, name, dark })
    : '';
  const fallbackPropsImage = fallback
    ? generatePublicImagePath({ ...fallback, dark })
    : '';

  const fallbackUrl = fallbackPropsImage || typeFallbackImage;

  const imageProps = shouldShowFallback
    ? {
        src: fallbackUrl,
        ...fallback,
        className: fallback?.className || className,
      }
    : { src: publicImagePath, alt, title, className };

  return (
    <img
      ref={ref}
      src={imageProps.src}
      title={imageProps.title || imageProps.alt}
      alt={imageProps.alt}
      className={imageProps.className}
      onError={() => {
        IMAGE_ERROR_MAP[publicImagePath] = true;

        setHasError(true);
      }}
      {...otherProps}
    />
  );
});
